import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { DeviceLabels } from "amazon-chime-sdk-component-library-react";
import { DeviceManager } from "../utils/DeviceManager";
import { usePlatformStore } from "@platform/store";
import { logVideoRoomError } from "../utils/logVideoRoomError";

interface DevicesContextType {
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  selectedAudioInputDevice: string | null;
  selectedVideoInputDevice: string | null;
  selectedAudioOutputDevice: string | null;
  isDevicesLoading: boolean;
  permissionDenied: boolean;
  setPermissionDenied: (denied: boolean) => void;
  listAndSelectDevices: (deviceLabels: DeviceLabels) => Promise<void>;
  selectAudioInputDevice: (deviceId: string) => Promise<void>;
  selectVideoInputDevice: (deviceId: string) => Promise<void>;
  selectAudioOutputDevice: (deviceId: string) => Promise<void>;
  forceRefreshDevices: () => Promise<void>;
}

const DevicesContext = createContext<DevicesContextType | null>(null);

export const DevicesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [deviceManager] = useState(() => new DeviceManager());
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    []
  );
  const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>(
    []
  );
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState<
    string | null
  >(null);
  const [selectedVideoInputDevice, setSelectedVideoInputDevice] = useState<
    string | null
  >(null);
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState<
    string | null
  >(null);
  const [isDevicesLoading, setIsDevicesLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Mutex to prevent concurrent listAndSelectDevices calls
  const isEnumeratingRef = useRef(false);

  const listAndSelectDevices = useCallback(async (deviceLabels: DeviceLabels) => {
    if (isEnumeratingRef.current) return;
    isEnumeratingRef.current = true;
    setIsDevicesLoading(true);
    try {
      await deviceManager.listAndSelectDevices(deviceLabels);
      setAudioInputDevices(deviceManager.getAudioInputDevices());
      setVideoInputDevices(deviceManager.getVideoInputDevices());
      setAudioOutputDevices(deviceManager.getAudioOutputDevices());

      const newSelectedAudioInput = deviceManager.getSelectedAudioInputDevice();
      const newSelectedVideoInput = deviceManager.getSelectedVideoInputDevice();
      const newSelectedAudioOutput = deviceManager.getSelectedAudioOutputDevice();

      setSelectedAudioInputDevice(newSelectedAudioInput);
      setSelectedVideoInputDevice(newSelectedVideoInput);
      setSelectedAudioOutputDevice(newSelectedAudioOutput);
    } finally {
      setIsDevicesLoading(false);
      isEnumeratingRef.current = false;
    }
  }, [deviceManager]);

  const selectAudioInputDevice = async (deviceId: string) => {
    await deviceManager.selectAudioInputDevice(deviceId);
    setSelectedAudioInputDevice(deviceId);
  };

  const selectVideoInputDevice = async (deviceId: string) => {
    await deviceManager.selectVideoInputDevice(deviceId);
    setSelectedVideoInputDevice(deviceId);
  };

  const selectAudioOutputDevice = async (deviceId: string) => {
    await deviceManager.selectAudioOutputDevice(deviceId);
    setSelectedAudioOutputDevice(deviceId);
  };

  const forceRefreshDevices = async () => {
    await deviceManager.forceRefreshDevices();
    setAudioInputDevices(deviceManager.getAudioInputDevices());
    setVideoInputDevices(deviceManager.getVideoInputDevices());
    setAudioOutputDevices(deviceManager.getAudioOutputDevices());
    setSelectedAudioInputDevice(deviceManager.getSelectedAudioInputDevice());
    setSelectedVideoInputDevice(deviceManager.getSelectedVideoInputDevice());
    setSelectedAudioOutputDevice(deviceManager.getSelectedAudioOutputDevice());
  };

  // Device state flows in one direction: DeviceManager (browser APIs)
  // → DevicesProvider (React reactivity) → Zustand (cross-feature access).
  // The momentary inconsistency between layers is sub-frame and acceptable.
  // Sync selected device IDs to Zustand store
  const setStoreAudioInput = usePlatformStore((s) => s.setSelectedAudioInputDevice);
  const setStoreVideoInput = usePlatformStore((s) => s.setSelectedVideoInputDevice);
  const setStoreAudioOutput = usePlatformStore((s) => s.setSelectedAudioOutputDevice);

  useEffect(() => {
    setStoreAudioInput(selectedAudioInputDevice);
  }, [selectedAudioInputDevice, setStoreAudioInput]);

  useEffect(() => {
    setStoreVideoInput(selectedVideoInputDevice);
  }, [selectedVideoInputDevice, setStoreVideoInput]);

  useEffect(() => {
    setStoreAudioOutput(selectedAudioOutputDevice);
  }, [selectedAudioOutputDevice, setStoreAudioOutput]);

  // Listen for device changes
  useEffect(() => {
    if (!navigator.mediaDevices) return;

    const handleDeviceChange = async () => {
      try {
        await listAndSelectDevices(DeviceLabels.AudioAndVideo);
      } catch (err) {
        logVideoRoomError(
          "Device change enumeration failed",
          "device_change_error",
          { error: String(err) }
        );
      }
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, [listAndSelectDevices]);

  const value = {
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
    selectedAudioInputDevice,
    selectedVideoInputDevice,
    selectedAudioOutputDevice,
    isDevicesLoading,
    permissionDenied,
    setPermissionDenied,
    listAndSelectDevices,
    selectAudioInputDevice,
    selectVideoInputDevice,
    selectAudioOutputDevice,
    forceRefreshDevices,
  };

  return (
    <DevicesContext.Provider value={value}>{children}</DevicesContext.Provider>
  );
};

export const useDevices = () => {
  const context = useContext(DevicesContext);
  if (!context) {
    throw new Error("useDevices must be used within a DevicesProvider");
  }
  return context;
};
